import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import get from 'lodash/get';
import { PropTypes } from 'prop-types';
import React, { useRef } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import styled, { ThemeConsumer } from 'styled-components';

import { StatusColor } from '@ge/tokens/colors/colors';
import { elevations } from '@ge/tokens/elevations';

const StyledInput = styled(TimePicker)`
  .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline {
    &:disabled {
      cursor: not-allowed;
    }
    ${({ errors, fieldArray, id }) => {
      const { fieldName, errorIndex, errorName } = fieldArray ?? {};
      return errors[id] || get(errors[fieldName], `${errorIndex}.${errorName}`)
        ? `border-color: ${StatusColor.DANGER} !important`
        : ``;
    }}
  }
  .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline {
    border-color: inherit;
  }
`;

const TimepickerWrapper = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 100px;
  width: 100px;

  .MuiInputBase-root {
    height: 28.5px;
    background: ${(props) => props.theme.datePicker.time.backgroundColor};
    color: ${(props) => props.theme.datePicker.time.OuterfontColor};
  }

  ,
  .MuiOutlinedInput-root {
    font-size: 12px;
  }

  .MuiInputBase-inputAdornedEnd {
    padding: 16.5px 6px !important;
  }

  .MuiSvgIcon-root {
    color: #888;
    width: 0.6em;
    height: 0.7em;
  }

  .MuiInputAdornment-root {
    margin-left: 0px;
  }

  .MuiMultiSectionDigitalClock-root > ul:nth-last-child(1)::after {
    height: inherit;
  }
`;

const customPopperStyled = (theme) => ({
  '& .MuiList-root': {
    background: theme?.datePicker?.time?.dropDownBackgroundColor,
    zIndex: elevations.P2,
  },
  '& .MuiMenuItem-root': {
    '&.Mui-selected': {
      ':hover': {
        border: '2px solid',
        borderColor: theme?.datePicker?.time?.highlightBorderColor,
        background: theme?.datePicker?.time?.highlightColor,
        borderRadius: '35px',
      },
      background: theme?.datePicker?.time?.highlightColor,
      color: theme?.datePicker?.time?.highlightTextColor,
      borderRadius: '35px',
    },
    color: theme?.datePicker?.time?.dropDownFontColor,
    fontSize: '12px',
    fontWeight: '400',
    lineHeight: '15px',
  },
  '& .MuiPickersPopper-paper': {
    backgroundColor: 'inherit',
  },
  '& .MuiList-root.MuiMultiSectionDigitalClockSection-root:hover': {
    scrollbarWidth: 'none',
  },
});

export const TimepickerStrMetaField = ({
  defaultValue,
  label,
  metadata,
  name,
  rules,
  fieldArray,
  style,
}) => {
  const { readOnly, required } = metadata;
  const { control, errors } = useFormContext();

  const CustomActionBar = () => null;
  const isAmPm = new Intl.DateTimeFormat(navigator.language, {
    hour: 'numeric',
  }).resolvedOptions().hour12;

  if (required) rules = { ...rules, required };
  return (
    <ThemeConsumer>
      {(theme) => (
        <TimepickerWrapper>
          <Controller
            control={control}
            defaultValue={defaultValue}
            name={name}
            rules={rules}
            render={({ onChange, value }) => {
              return (
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <StyledInput
                    style={style}
                    errors={errors}
                    fieldArray={fieldArray}
                    required={required}
                    id={name}
                    value={value}
                    ampm={isAmPm}
                    onChange={onChange}
                    slots={{
                      actionBar: CustomActionBar,
                    }}
                    slotProps={{
                      popper: {
                        sx: customPopperStyled(theme),
                        disablePortal: true,
                      },
                    }}
                  />
                </LocalizationProvider>
              );
            }}
          />
        </TimepickerWrapper>
      )}
    </ThemeConsumer>
  );
};

TimepickerStrMetaField.propTypes = {
  name: PropTypes.string.isRequired,
  defaultValue: PropTypes.string,
  label: PropTypes.string,
  metadata: PropTypes.instanceOf(Object).isRequired,
  rules: PropTypes.instanceOf(Object),
};

TimepickerStrMetaField.defaultProps = {
  defaultValue: undefined,
  label: '',
  rules: null,
};
